import React, { useContext } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Login from '../pages/Login';
import Register from '../pages/Register';
import AcceptAccountInvitation from '../pages/AcceptAccountInvitation';
import ForgotPassword from '../pages/ForgotPassword';
import ResetPassword from '../pages/ResetPassword';
import Properties from '../pages/Properties';
import PropertyDetails from '../pages/PropertyDetails';
import BankAccountList from '../pages/BankAccountList';
import EditRentalAccount from '../pages/EditRentalAccount';
import Admin from '../pages/Admin';
import OccupantList from '../pages/OccupantList';
import AgencyList from '../pages/AgencyList';
import AgencyDetails from '../pages/AgencyDetails';
import UserManagement from '../pages/UserManagement';
import RoleManagement from '../pages/RoleManagement';
import SubscriptionManagement from '../pages/SubscriptionManagement';
import SubscriptionSettings from '../pages/SubscriptionSettings';
import Reports from '../pages/Reports';
import RentalListingsReport from '../pages/RentalListingsReport';
import PaymentValidationPage from '../pages/PaymentValidationPage';
import { AuthContext } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useSubscription } from '../hooks/useSubscription';

/**
 * Protected Route component with role-based access control
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {boolean} props.requireAgent - Requires Agent or System Admin role
 * @param {boolean} props.requireAdmin - Requires System Admin role
 */
const ProtectedRoute = ({ children, requireAgent = false, requireAdmin = false, requirePlan }) => {
    const { user } = useContext(AuthContext);
    const { canAccessAgentFeatures, canAccessAdminFeatures } = usePermissions();
    const { meetsMinimum } = useSubscription();

    // Not logged in - redirect to login
    if (!user) {
        return <Navigate to="/login" />;
    }

    // Check admin requirement
    if (requireAdmin && !canAccessAdminFeatures) {
        return <Navigate to="/properties" />;
    }

    // Check agent requirement
    if (requireAgent && !canAccessAgentFeatures) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-2">Access Denied</h2>
                    <p className="text-gray-500">You do not have permission to access this page.</p>
                </div>
            </div>
        );
    }

    // Check subscription plan requirement
    if (requirePlan && !meetsMinimum(requirePlan)) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-2">Subscription Required</h2>
                    <p className="text-gray-500">This feature requires a <strong>{requirePlan}</strong> plan or higher.</p>
                </div>
            </div>
        );
    }

    return children;
};

const AppRoutes = () => (
    <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/RentalManagement" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/accept-invitation" element={<AcceptAccountInvitation />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route path="/" element={
            <ProtectedRoute>
                <Navigate to="/properties" />
            </ProtectedRoute>
        } />

        {/* Agent Features - Require Agent or System Admin role */}
        <Route path="/properties" element={
            <ProtectedRoute requireAgent>
                <Properties />
            </ProtectedRoute>
        } />

        <Route path="/properties/:id" element={
            <ProtectedRoute requireAgent>
                <PropertyDetails />
            </ProtectedRoute>
        } />

        <Route path="/bankList" element={
            <ProtectedRoute requireAgent>
                <BankAccountList />
            </ProtectedRoute>
        } />

        <Route path="/bankList/edit/:id" element={
            <ProtectedRoute requireAgent>
                <EditRentalAccount />
            </ProtectedRoute>
        } />

        <Route path="/occupants" element={
            <ProtectedRoute requireAgent>
                <OccupantList />
            </ProtectedRoute>
        } />

        {/* Admin Features - Require System Administrator role */}
        <Route path="/admin" element={
            <ProtectedRoute requireAdmin>
                <Admin />
            </ProtectedRoute>
        } />

        <Route path="/agencies" element={
            <ProtectedRoute requireAdmin>
                <AgencyList />
            </ProtectedRoute>
        } />

        <Route path="/agencies/:id" element={
            <ProtectedRoute requireAdmin>
                <AgencyDetails />
            </ProtectedRoute>
        } />

        <Route path="/admin/users" element={
            <ProtectedRoute requireAdmin>
                <UserManagement />
            </ProtectedRoute>
        } />

        <Route path="/admin/roles" element={
            <ProtectedRoute requireAdmin>
                <RoleManagement />
            </ProtectedRoute>
        } />

        <Route path="/admin/subscriptions" element={
            <ProtectedRoute requireAdmin>
                <SubscriptionManagement />
            </ProtectedRoute>
        } />

        {/* Subscription settings — accessible to all authenticated users */}
        <Route path="/subscription" element={
            <ProtectedRoute>
                <SubscriptionSettings />
            </ProtectedRoute>
        } />

        {/* Reports - Require Agent or System Admin role */}
        <Route path="/reports" element={
            <ProtectedRoute requireAgent>
                <Reports />
            </ProtectedRoute>
        } />

        <Route path="/reports/rental-listings" element={
            <ProtectedRoute requireAgent>
                <RentalListingsReport />
            </ProtectedRoute>
        } />

        <Route path="/payment-validation" element={
            <ProtectedRoute requireAgent>
                <PaymentValidationPage />
            </ProtectedRoute>
        } />
    </Routes>
);

export default AppRoutes;